/**
 * @module Model
 */

import { AgentRequestAction, UserAgentRef, UserBulkAction, UserPair, OrderUser } from '@feature/users/shared';
import { EmailCC, Profile } from '@feature/profiles/shared';
import { Document } from '@feature/document-center/shared/document.model';

/**
 * Type representing any user in the application.
 *
 * This type is a union of different user-related types, allowing for flexibility in handling
 * various user representations within the application. It can be used to define variables or
 * parameters that may accept any of the specified user types.
 *
 * @type {User | OrderUser | UserPair}
 */
export type AnyUser = User | OrderUser | UserPair;

/**
 * Class representing an action associated with an agent request, including user details.
 *
 * This class encapsulates the details of an agent request action along with the associated
 * user information. It is used to manage actions taken on agent requests within the application.
 *
 * @class
 * @model
 */
export class AgentRequestActionWithUser {
	/**
	 * The details of the agent associated with the request.
	 * @type {UserAgentRef}
	 */
	agentDetails: UserAgentRef;

	/**
	 * The action being performed on the agent request.
	 * @type {AgentRequestAction}
	 */
	action: AgentRequestAction;

	/**
	 * Creates an instance of the AgentRequestActionWithUser class.
	 *
	 * @constructor
	 * @param {UserAgentRef} agentDetails - The details of the agent associated with the request.
	 * @param {AgentRequestAction} action - The action being performed on the agent request.
	 */
	constructor(agentDetails: UserAgentRef, action: AgentRequestAction) {
		this.agentDetails = agentDetails;
		this.action = action;
	}
}

/**
 * Class representing a user in the application.
 *
 * This class encapsulates various properties related to a user, including personal information,
 * account status, roles, and other relevant data. It provides methods to access the user's roles
 * and their authority levels.
 *
 * @class
 * @model
 * @note Don't mistreat with the Auth0 user class.
 */
export class User {
	/**
	 * The unique identifier for the user.
	 * @type {number}
	 */
	id: number;

	/**
	 * The first name of the user.
	 * @type {string}
	 */
	firstName: string;

	/**
	 * The middle name of the user (optional).
	 * @type {string}
	 */
	middleName?: string;

	/**
	 * The last name of the user.
	 * @type {string}
	 */
	lastName: string;

	/**
	 * The full name of the user, typically a concatenation of
	 * first, middle, and last names.
	 * @type {string}
	 */
	fullName: string;

	/**
	 * The email address of the user.
	 * @type {string}
	 */
	email: string;

	/**
	 * Indicates whether the user is new.
	 * @type {boolean}
	 */
	isNew: boolean;

	/**
	 * Indicates whether the user is banned.
	 * @type {boolean}
	 */
	isBanned: boolean;

	/**
	 * Indicates whether the user has migrated.
	 * @type {boolean}
	 */
	isMigrated: boolean;

	/**
	 * Indicates whether the user is an individual.
	 * @type {boolean}
	 */
	isIndividual: boolean;

	/**
	 * Indicates whether the user is a company.
	 * @type {boolean}
	 */
	isCompany: boolean;

	/**
	 * Indicates whether the user is a member.
	 * @type {boolean}
	 */
	isMember: boolean;

	/**
	 * The time zone of the user, if applicable.
	 *
	 * @example Use for date based on timeZone extraction:
	 * new Date().toLocaleString('en', { timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone });
	 *
	 * @see [Getting the client's time zone](https://stackoverflow.com/a/34602679/15391077)
	 * @see [Initialize Date to a particular time zone](https://stackoverflow.com/a/15171030/15391077)
	 */
	timeZone?: string;

	/**
	 * Indicates whether the user is an agent.
	 * @type {boolean}
	 */
	isAgent: boolean;

	/**
	 * Indicates whether the user is a talent.
	 * @type {boolean}
	 */
	isTalent: boolean;

	/**
	 * Indicates whether the user is a customer.
	 * @type {boolean}
	 */
	isCustomer: boolean;

	/**
	 * Indicates whether the user is an admin.
	 * @type {boolean}
	 */
	isAdmin: boolean;

	/**
	 * Indicates whether the user can be an agent.
	 * @type {boolean}
	 */
	canBeAgent: boolean;

	/**
	 * Indicates whether the user can be a customer.
	 * @type {boolean}
	 */
	canBeCustomer: boolean;

	/**
	 * Indicates the preference that controls the display options for non-performance payment.
	 * @type {boolean}
	 */
	canBePaidAsVendor?: boolean;

	/**
	 * Indicates the preference that controls the access to non-performance payment.
	 * @type {boolean}
	 */
	canPayOrRequestAsVendor?: boolean;

	/**
	 * The current signup step for the user.
	 *
	 * This property is used to track the user's progress in the signup process.
	 *
	 * @type {string}
	 * @note Has to be pulled separately for individual.
	 */
	signupStep: string;

	/**
	 * The count of orders associated with the payee user.
	 * @type {number}
	 */
	payeeOrdersCount: number;

	individual?: Individual;

	/**
	 * An array of roles assigned to the user.
	 * @type {Array<UserRole>}
	 */
	roles?: Array<UserRole>;

	/**
	 * An array of agents associated with the client user.
	 * @type {Array<UserAgentRef>}
	 */
	agents?: Array<UserAgentRef>;

	/**
	 * An array of clients associated with the agent user.
	 * @type {Array<UserAgentRef>}
	 */
	clients?: Array<UserAgentRef>;

	/**
	 * An array of email CCs associated with the user.
	 * @type {Array<EmailCC>}
	 */
	emailCCs?: Array<EmailCC>;

	/**
	 * Optional JWT token used for the user's authorization.
	 * @type {string}
	 */
	jwt?: string;

	/**
	 * Optional billing address associated with the user (to be used in AnyUser).
	 */
	billAddress?: any;

	/**
	 * Optional company details associated with the owner.
	 */
	company?: any;

	/**
	 * Indicates whether the user has been checked.
	 *
	 * @type {boolean}
	 * @note Can be used with the bulk actions.
	 */
	checked?: boolean;

	/**
	 * Indicates the status of the user.
	 * @type {boolean}
	 */
	status?: boolean;

	/**
	 * Gets the role with the minimum level, indicating the highest authority.
	 *
	 * @getter
	 * @returns {UserRole | null} The role with the minimum level or null if no roles exist.
	 */
	get minRole(): UserRole {
		if (this.roles) {
			return this.roles.reduce((prev, curr) => {
				return prev.level < curr.level ? prev : curr;
			});
		}
		return null;
	}

	/**
	 * Gets the role with the maximum level, indicating the lowest authority.
	 *
	 * @getter
	 * @returns {UserRole | null} The role with the maximum level or null if no roles exist.
	 */
	get maxRole(): UserRole {
		if (this.roles) {
			return this.roles.reduce((prev, curr) => {
				return prev.level > curr.level ? prev : curr;
			});
		}
		return null;
	}
}

/**
 * Class representing a user role within the application.
 *
 * This class encapsulates the properties associated with a user role.
 * It is used to manage and define roles that users can have in the application.
 *
 * @class
 * @model
 */
export class UserRole {
	/**
	 * The unique identifier for the user role.
	 * @type {number}
	 */
	id: number;

	/**
	 * The level of authority associated with the user role.
	 * @type {number}
	 */
	level: number;

	/**
	 * The alias or shorthand name for the user role.
	 * @type {string}
	 */
	alias: string;

	/**
	 * The title of the user role.
	 * @type {string}
	 */
	title: string;

	/**
	 * A description of the user role.
	 * @type {string}
	 */
	description: string;

	/**
	 * Creates an instance of the UserRole class.
	 *
	 * @constructor
	 * @param {string} alias - The alias for the user role.
	 */
	constructor(alias: string) {
		this.alias = alias;
	}
}

/**
 * Class representing an individual user in the application.
 *
 * This class encapsulates various properties related to an individual, including their unique
 * identifier, associated user ID, status as a new user, roles, and profile information. It is
 * used to manage and represent individual users within the application.
 *
 * @class
 * @model
 */
export class Individual {
	/**
	 * The unique identifier for the individual.
	 * @type {number}
	 */
	id: number;

	/**
	 * The user ID associated with the individual.
	 * @type {number}
	 */
	userId: number;

	/**
	 * Indicates whether the user is new.
	 *
	 * @note
	 * If the flag is ON, users won't be displayed as a Direct Pay in the payment pull-down.
	 * Once the client has finished the sign-up, the flag is switched OFF.
	 *
	 * @type {boolean}
	 */
	userIsNew: boolean;

	/**
	 * Optional array of roles assigned to the user.
	 * @type {Array<UserRole> | undefined}
	 */
	userRoles?: Array<UserRole>;

	/**
	 * The profile information associated with the individual.
	 * @type {Profile}
	 */
	profile: Profile;
}

/**
 * Class representing the verification status of an Auth0 user.
 *
 * This class encapsulates properties related to the user's email verification status,
 * including the user's email address and whether the email has been verified. It is used
 * to manage and represent the verification state of users authenticated through Auth0.
 *
 * @class
 * @model
 */
export class Auth0UserVerification {
	/**
	 * The email address of the user.
	 * @type {string}
	 */
	email: string;

	/**
	 * Indicates whether the user's email has been verified.
	 * @type {boolean}
	 */
	emailVerified: boolean;
}

/**
 * Class representing the response from an add user operation.
 *
 * This class encapsulates the result of attempting to add a user, including whether the
 * addition was successful, any error messages, warning messages, and the user object if
 * the addition was successful. It is used to provide feedback to the application regarding
 * the outcome of the add user request.
 *
 * @class
 * @model
 */
export class AddUserResponse {
	/**
	 * Indicates whether the user can be added successfully.
	 * @type {boolean}
	 */
	canAdd: boolean;

	/**
	 * Optional error message returned if the user cannot be added.
	 * @type {string | undefined}
	 */
	errorMessage?: string;

	/**
	 * Optional warning message that may be provided during the add user operation.
	 * @type {string | undefined}
	 */
	warningMessage?: string;

	/**
	 * Optional user object representing the newly added user.
	 * @type {User | undefined}
	 */
	user?: User;
}

/**
 * Class representing a user along with their associated document.
 *
 * It is used to manage and represent the relationship between
 * users and their latest uploaded document. Helps in getting access to the document.
 *
 * @class
 * @model
 */
export class UserWithDocument {
	/**
	 * The user object.
	 * @type {User}
	 */
	user: User;

	/**
	 * The document object associated with the user.
	 * @type {Document}
	 */
	document: Document;
}

/**
 * Class representing the input for bulk actions on users.
 *
 * This class encapsulates the details of a bulk action to be performed on a list of users,
 * including the specific action type and the array of user IDs that the action will apply to.
 *
 * @class
 * @model
 */
export class UserBulkActionInput {
	/**
	 * The action to be performed on the users.
	 * @type {UserBulkAction}
	 */
	action: UserBulkAction;

	/**
	 * An array of user IDs that the bulk action will apply to.
	 * @type {Array<number>}
	 */
	users: Array<number>;
}

/**
 * Class representing the output for bulk actions on users.
 *
 * This class encapsulates the details of a bulk action that were performed on a list of users,
 * including the array of affected user objects and unaffected user IDs.
 *
 * @class
 * @model
 */
export class UserBulkActionOutput {
	/**
	 * An array of affected user objects.
	 * @type {Array<User>}
	 */
	affectedUsers: Array<User>;

	/**
	 * An array of unaffected user IDs.
	 * @type {Array<number>}
	 */
	unaffectedUsers: Array<number>;
}

/**
 * Class representing a relationship between the user and profile context.
 *
 * This class encapsulates information about the currently authenticated user and associated profile.
 *
 * @class
 * @model
 */
export class UserContextRef {
	/**
	 * The unique identifier for the user (optional).
	 * @type {number | undefined}
	 */
	userId?: number;

	/**
	 * The profile associated with the user (optional).
	 * @type {Profile | undefined}
	 */
	profile?: Profile;
}
